import { useState } from 'react';
import company from '../data/company';
import content from '../data/content';
import { ownProjects } from '../data/projects';
import services from '../data/services';
import Button from '../components/Button';
import ContactBlock from '../components/ContactBlock';
import PageMeta from '../components/PageMeta';
import Reveal from '../components/Reveal';
import SectionHeading from '../components/SectionHeading';
import WorkCard from '../components/WorkCard';
import './Home.css';

function CheckIcon() {
  return (
    <svg
      className="tender__check"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      aria-hidden="true"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function Home() {
  const [showAllServices, setShowAllServices] = useState(false);
  const visibleServices = showAllServices ? services : services.slice(0, 5);
  const copy = content.home;

  return (
    <>
      <PageMeta
        title={copy.metaTitle}
        description={copy.metaDescription}
      />

      <section className="hero">
        <div className="hero__grid" aria-hidden="true" />
        <div className="hero__glow" aria-hidden="true" />

        <div className="container hero__inner">
          <Reveal as="p" className="eyebrow">
            {copy.heroEyebrow}
          </Reveal>

          <Reveal as="h1" className="display hero__title" delay={60}>
            {copy.heroTitle[0]}
            <span className="hero__accent">{copy.heroTitle[1]}</span>
            {copy.heroTitle[2]}
            <span className="hero__accent">{copy.heroTitle[3]}</span>
          </Reveal>

          <Reveal as="p" className="lead hero__lead" delay={120}>
            {company.description}
          </Reveal>

          <Reveal className="hero__stats" delay={240}>
            {company.stats.map((stat) => (
              <div
                className={`stat${stat.verified ? '' : ' is-placeholder'}`}
                key={stat.label}
                title={stat.verified ? undefined : copy.placeholderStatTitle}
              >
                <div className="stat__value">{stat.value}</div>
                <div className="stat__label">{stat.label}</div>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      <div className="strip">
        <div className="container strip__inner">
          {services.slice(0, 5).map((service) => (
            <span className="strip__item" key={service.slug}>
              {service.tag}
            </span>
          ))}
        </div>
      </div>

      <section className="section" id="services">
        <div className="container">
          <SectionHeading
            title={copy.servicesTitle}
            description={copy.servicesDescription}
          />

          <ul className="services" id="services-list">
            {visibleServices.map((service, index) => (
              <Reveal as="li" className="service edge-hover" key={service.slug} delay={index * 40}>
                <span className="service__num">{String(index + 1).padStart(2, '0')}</span>
                <span className="service__title">{service.title}</span>
                <span className="service__desc">{service.short}</span>
              </Reveal>
            ))}
          </ul>
          {services.length > 5 && (
            <Button
              variant="ghost"
              size="sm"
              className="services__toggle"
              aria-expanded={showAllServices}
              aria-controls="services-list"
              onClick={() => setShowAllServices((isExpanded) => !isExpanded)}
            >
              {showAllServices
                ? copy.hideServices
                : copy.showServices(services.length - 5)}
            </Button>
          )}
        </div>
      </section>

      <section className="section section--line">
        <div className="container">
          <SectionHeading
            title={copy.processTitle}
            description={copy.processDescription}
          />

          <ol className="process">
            {copy.process.map((step, index) => (
              <Reveal
                as="li"
                className="process__step edge-hover"
                key={step.title}
                delay={index * 60}
              >
                <span className="process__num">{String(index + 1).padStart(2, '0')}</span>
                <h3 className="process__title">{step.title}</h3>
                <p className="process__text">{step.text}</p>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      <section className="section section--line">
        <div className="container">
          <SectionHeading
            title={copy.productsTitle}
            description={copy.productsDescription}
          />

          <div className="works">
            {ownProjects.map((project) => (
              <WorkCard project={project} key={project.slug} />
            ))}
          </div>
        </div>
      </section>

      <section className="section section--line" id="tenders">
        <div className="container">
          <div className="tender edge-hover">
            <div>
              <h2 className="tender__title">
                {copy.tenderTitle}
              </h2>
              <p className="tender__text">
                {copy.tenderDescription}
              </p>

              <ul className="tender__list">
                {copy.tenderPoints.map((point) => (
                  <li className="tender__item" key={point}>
                    <CheckIcon />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="tender__panel">
              <p className="eyebrow">{copy.requisitesTitle}</p>
              <dl className="tender__req">
                <div>
                  <dt>{copy.companyNameLabel}</dt>
                  <dd>{company.fullName}</dd>
                </div>
                <div>
                  <dt>{copy.innLabel}</dt>
                  <dd>{company.inn}</dd>
                </div>
                <div>
                  <dt>{copy.kppLabel}</dt>
                  <dd>{company.kpp}</dd>
                </div>
                <div>
                  <dt>{copy.ogrnLabel}</dt>
                  <dd>{company.ogrn}</dd>
                </div>
                <div>
                  <dt>{copy.okvedLabel}</dt>
                  <dd>{company.okved.split(' — ')[0]}</dd>
                </div>
              </dl>
              <p className="tender__note">
                {copy.tenderNote}
              </p>
            </div>
          </div>
        </div>
      </section>

      <ContactBlock />
    </>
  );
}

export default Home;
